/**
 * Markdown-Editor (v1.3.0 Block 4, Feature #14, D-20).
 *
 * `files.kind = 'md'` ist nur ein weiterer Wert des offenen Diskriminators —
 * die Datei erbt damit Ordnerbaum, Guard, ACL, Freigabe, Umbenennen/Verschieben
 * und Papierkorb/Archiv, ohne dass hier irgendetwas davon wiederholt wird. Der
 * Inhalt liegt wie Board und Bild auf S3 (Konsistenz vor Entkopplung), es gibt
 * keine eigene Tabelle.
 *
 * Aufbau nach dem Board-Muster: eigene Seite außerhalb der RyfeShell, oben die
 * `.ryfe-board__bar`. Bewusst slim — Toolbar, Slash-Commands, Bilder-in-MD und
 * Embeds sind vertagt.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";

import { ApiError, ryfeApi } from "../api";
import {
  downloadBytes,
  downloadText,
  mdToPdf,
  safeFilename,
} from "../file-export";
import { escapeHtml, renderMarkdown } from "../markdown";
import {
  adjacentTableCell,
  DEFAULT_DOC_SETTINGS,
  domToMarkdown,
  parseDocSettings,
  pdfOptionsFor,
  withDocSettings,
  type DocSettings,
} from "../md-doc";
import { backToFiles, navigate } from "../router";
import { serializeScene } from "../board-data";
import { routeForKind } from "../open-file";
import { useRyfe } from "../RyfeContext";
import { useDocumentTitle } from "../use-doc-title";
import { createEncryptedSnapshot } from "../share";

import { ShareSubmenu } from "./ShareSubmenu";
import { AddFileDialog, type NewEntryKind } from "./AddFileDialog";
import { RyfeDocEmbed, type EmbedMode } from "./RyfeDocEmbed";
import {
  LinkSettingsDialog,
  linkMarkdown,
  type LinkSettings,
  type PreviewMode,
} from "./LinkSettingsDialog";

import "./ryfe.scss";

const AUTOSAVE_MS = 1200;

/** Datei-Id aus einer internen RYFE-Route (/md/… /list/… /board/…), sonst null. */
const idFromRoute = (href: string): string | null => {
  const m = /^\/(?:md|list|board)\/([^/?#]+)/.exec(href);
  return m ? m[1] : null;
};

export const RyfeMarkdown = ({ fileId }: { fileId: string }) => {
  const { workspace, workspaces } = useRyfe();
  const [wsId, setWsId] = useState<string | null>(null);
  const [name, setName] = useState("Notiz");
  const [docFolder, setDocFolder] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Revision R3 — „wysiwyg" (Editor formatiert) ist die Standard-Ansicht: man
  // schreibt direkt im gerenderten Dokument; Markdown bleibt Speicherformat.
  const [view, setView] = useState<"wysiwyg" | "split" | "edit" | "preview">(
    "wysiwyg",
  );
  // Revision R3 — Datei-Menü + Dokumenteinstellungen (Frontmatter, md-doc).
  const [fileMenu, setFileMenu] = useState(false);
  const [docDialog, setDocDialog] = useState<DocSettings | null>(null);
  // Welle B — „＋ Datei erstellen": create a Board/Notiz/Liste (default in the
  // same folder as this note) and drop an inline link to it into the document.
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  // Welle B (Punkt 15) — Link-Darstellungs-Dialog (Link/Button/Vorschau).
  // `el` (nur WYSIWYG) = das bestehende Element, das ersetzt wird; sonst neu.
  const [linkDialog, setLinkDialog] = useState<{
    initial: LinkSettings;
    el: HTMLElement | null;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mdInput = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const wysiwygRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const embedRoots = useRef<Root[]>([]);
  const imgInput = useRef<HTMLInputElement | null>(null);

  const { settings: docSettings, body: docBody } = parseDocSettings(text);
  // R4 — Browser-Tab trägt den Dateinamen.
  useDocumentTitle(name);

  // Laden — dieselbe Workspace-Auflösung wie beim Board (eine per Freigabe
  // geteilte Notiz kann in einem fremden Mandanten liegen).
  // Revision R3 — GENAU EINMAL je Datei laden: ändert der Context seine
  // Objekt-Identität (z. B. Auth-Refresh), darf der Re-Run nicht den
  // Server-Stand über ungespeicherte lokale Eingaben legen.
  const loadedForRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const active = workspace?.id;
    // Erst nach ERFOLGREICHEM Laden gesetzt (StrictMode-Doppel-Mount bricht
    // den ersten Lauf ab — der zweite muss noch laden dürfen).
    if (!active || loadedForRef.current === fileId) {
      return;
    }
    (async () => {
      const candidates = [
        active,
        ...workspaces.map((w) => w.id).filter((id) => id !== active),
      ];
      try {
        const { items } = await ryfeApi.sharedWithMe();
        for (const it of items) {
          if (!candidates.includes(it.workspaceId)) {
            candidates.push(it.workspaceId);
          }
        }
      } catch {
        /* Freigabe-Liste optional */
      }
      for (const cand of candidates) {
        try {
          const meta = await ryfeApi.getFile(cand, fileId);
          const buf = await ryfeApi.getFileContent(cand, fileId);
          if (cancelled) {
            return;
          }
          setWsId(cand);
          setName(meta.name);
          setDocFolder((meta as { folder_id?: string }).folder_id ?? null);
          setText(new TextDecoder().decode(new Uint8Array(buf)));
          setStatus("ready");
          loadedForRef.current = fileId;
          return;
        } catch {
          /* nächster Workspace */
        }
      }
      if (!cancelled) {
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileId, workspace?.id, workspaces]);

  const save = useCallback(
    async (value: string) => {
      if (!wsId) {
        return;
      }
      setSaveState("saving");
      try {
        // CC_05 Block 1 — assert 'md' (symmetrical to the board barrier).
        await ryfeApi.putFileContent(
          wsId,
          fileId,
          value,
          "text/markdown",
          "md",
        );
        setSaveError(null);
        setSaveState("saved");
      } catch (err) {
        // Block 1 (D-21): ein fehlgeschlagenes Speichern ist nie stumm.
        setSaveError(
          err instanceof ApiError
            ? `Speichern fehlgeschlagen (${err.status}): ${err.message}`
            : "Speichern fehlgeschlagen — keine Verbindung zum Server.",
        );
        setSaveState("error");
      }
    },
    [wsId, fileId],
  );

  const onChange = (value: string) => {
    setText(value);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => void save(value), AUTOSAVE_MS);
  };

  // P11.1 (Lane-C-Mock) — Toolbar: selektionsbewusstes Einfügen von Markdown.
  // `wrap` umschließt die Auswahl (Fett/Kursiv/Code), `linePrefix` setzt bzw.
  // ersetzt den Präfix der aktuellen Zeile (Absatz/H1–H3/Checkliste), `block`
  // fügt ein Skelett (Tabelle) unterhalb der aktuellen Zeile ein.
  const applyMd = (
    action:
      | { wrap: string }
      | { linePrefix: string }
      | { block: string }
      | { around: [string, string] }
      | { insert: string },
  ) => {
    const ta = editorRef.current;
    if (!ta) {
      return;
    }
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? start;
    let next = text;
    let cursor = end;
    if ("wrap" in action) {
      const sel = text.slice(start, end) || "Text";
      next =
        text.slice(0, start) +
        action.wrap +
        sel +
        action.wrap +
        text.slice(end);
      cursor = start + action.wrap.length + sel.length + action.wrap.length;
    } else if ("around" in action) {
      // R4 (Punkt 11) — asymmetrisches Umschließen (Links: [sel](url)).
      const sel = text.slice(start, end) || "Link";
      next =
        text.slice(0, start) +
        action.around[0] +
        sel +
        action.around[1] +
        text.slice(end);
      cursor =
        start + action.around[0].length + sel.length + action.around[1].length;
    } else if ("insert" in action) {
      // R4 — Einfügen an der Cursor-Position (Bilder).
      next = text.slice(0, start) + action.insert + text.slice(end);
      cursor = start + action.insert.length;
    } else if ("linePrefix" in action) {
      const lineStart = text.lastIndexOf("\n", start - 1) + 1;
      const rest = text.slice(lineStart);
      const stripped = rest.replace(/^(#{1,6} |- \[ \] |- )/, "");
      next = text.slice(0, lineStart) + action.linePrefix + stripped;
      cursor = lineStart + action.linePrefix.length;
    } else {
      const lineEnd = text.indexOf("\n", end);
      const at = lineEnd === -1 ? text.length : lineEnd;
      next = `${text.slice(0, at)}\n\n${action.block}\n${text.slice(at)}`;
      cursor = at + 2 + action.block.length;
    }
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const TABLE_SKELETON = [
    "| Spalte 1 | Spalte 2 | Spalte 3 |",
    "| --- | --- | --- |",
    "|  |  |  |",
  ].join("\n");

  // ---- Revision R3: WYSIWYG-Modus ------------------------------------------
  // contentEditable auf dem gerenderten HTML; jede Eingabe wird über
  // domToMarkdown zurück in Markdown serialisiert (Markdown bleibt die eine
  // Quelle der Wahrheit — Speichern/MCP/Snapshot unverändert). Der
  // Frontmatter-Prefix (Dokumenteinstellungen) bleibt dabei erhalten.
  const syncFromWysiwyg = () => {
    const el = wysiwygRef.current;
    if (!el) {
      return;
    }
    const body = domToMarkdown(el);
    const prefix = text.slice(0, text.length - docBody.length);
    onChange(prefix + body);
  };

  // DOM nur beim BETRETEN des Modus (bzw. nach Laden/Import) neu setzen —
  // nie pro Tastendruck, sonst springt der Cursor.
  const textRef = useRef(text);
  textRef.current = text;
  const fillWysiwyg = useCallback((md: string) => {
    const el = wysiwygRef.current;
    if (el) {
      el.innerHTML = renderMarkdown(parseDocSettings(md).body);
    }
  }, []);
  useEffect(() => {
    if (view === "wysiwyg" && status === "ready") {
      fillWysiwyg(textRef.current);
    }
  }, [view, status, fillWysiwyg]);

  // Welle B — create a linked file (Board/Notiz/Liste/Ordner) in this note's
  // folder and append an inline markdown link to it. The created file is NOT
  // navigated to — the point is to build documents that reference each other.
  const createLinked = async (kind: NewEntryKind, docName: string) => {
    if (!wsId || !docFolder) {
      return;
    }
    setCreateBusy(true);
    try {
      let route: string | null = null;
      if (kind === "folder") {
        const f = await ryfeApi.createFolder(wsId, docFolder, docName);
        route = `/files/${f.id}`;
      } else {
        const seed =
          kind === "md"
            ? `# ${docName}\n\n`
            : kind === "list"
            ? ""
            : serializeScene([], {}, {});
        const contentType =
          kind === "md" ? "text/markdown" : "application/json";
        const f = await ryfeApi.createFile(
          wsId,
          docFolder,
          docName,
          kind,
          seed,
          contentType,
        );
        route = routeForKind(kind, f.id);
      }
      if (route) {
        const linkMd = `[${docName}](${route})`;
        const base = textRef.current;
        const next =
          base === "" || base.endsWith("\n")
            ? `${base}${linkMd}\n`
            : `${base}\n\n${linkMd}\n`;
        onChange(next);
        if (view === "wysiwyg") {
          fillWysiwyg(next);
        }
      }
      setCreating(false);
    } catch (err) {
      setSaveError(
        err instanceof ApiError
          ? `Anlegen fehlgeschlagen (${err.status}): ${err.message}`
          : "Anlegen fehlgeschlagen — keine Verbindung zum Server.",
      );
    } finally {
      setCreateBusy(false);
    }
  };

  const execWysiwyg = (cmd: string, val?: string) => {
    wysiwygRef.current?.focus();
    document.execCommand(cmd, false, val);
    syncFromWysiwyg();
  };

  // EIN Format-Verteiler für beide Editor-Arten: im Text-Modus Markdown-
  // Manipulation (applyMd), im WYSIWYG-Modus execCommand auf der Auswahl.
  type Fmt =
    | "p"
    | "h1"
    | "h2"
    | "h3"
    | "bold"
    | "italic"
    | "code"
    | "ul"
    | "check"
    | "table"
    | "quote"
    | "link";
  const format = (kind: Fmt) => {
    if (view === "wysiwyg") {
      switch (kind) {
        case "p":
          return execWysiwyg("formatBlock", "P");
        case "h1":
        case "h2":
        case "h3":
          return execWysiwyg("formatBlock", kind.toUpperCase());
        case "bold":
          return execWysiwyg("bold");
        case "italic":
          return execWysiwyg("italic");
        case "code": {
          const sel = window.getSelection()?.toString() || "Code";
          return execWysiwyg("insertHTML", `<code>${escapeHtml(sel)}</code>`);
        }
        case "ul":
          return execWysiwyg("insertUnorderedList");
        case "check":
          // Bug-Fix: im WYSIWYG-Modus muss die Checkbox als ECHTE Checkbox
          // eingefügt werden — nicht als Literaltext „[ ]". renderMarkdown
          // erzeugt aus „- [ ]" ein <input type=checkbox> (markdown.ts).
          return execWysiwyg("insertHTML", renderMarkdown("- [ ] Aufgabe"));
        case "table":
          return execWysiwyg("insertHTML", renderMarkdown(TABLE_SKELETON));
        case "quote":
          return execWysiwyg("formatBlock", "BLOCKQUOTE");
        case "link": {
          // R4 (Punkt 11) — Link auf die Auswahl setzen.
          const url = window.prompt("Link-URL (https://…)", "https://") || "";
          if (!url.trim() || url.trim() === "https://") {
            return;
          }
          const sel = window.getSelection()?.toString() || url.trim();
          return execWysiwyg(
            "insertHTML",
            `<a href="${escapeHtml(url.trim())}">${escapeHtml(sel)}</a>`,
          );
        }
      }
      return;
    }
    switch (kind) {
      case "p":
        return applyMd({ linePrefix: "" });
      case "h1":
        return applyMd({ linePrefix: "# " });
      case "h2":
        return applyMd({ linePrefix: "## " });
      case "h3":
        return applyMd({ linePrefix: "### " });
      case "bold":
        return applyMd({ wrap: "**" });
      case "italic":
        return applyMd({ wrap: "*" });
      case "code":
        return applyMd({ wrap: "`" });
      case "ul":
        return applyMd({ linePrefix: "- " });
      case "check":
        return applyMd({ linePrefix: "- [ ] " });
      case "table":
        return applyMd({ block: TABLE_SKELETON });
      case "quote":
        return applyMd({ linePrefix: "> " });
      case "link": {
        const url = window.prompt("Link-URL (https://…)", "https://") || "";
        if (!url.trim() || url.trim() === "https://") {
          return;
        }
        return applyMd({ around: ["[", `](${url.trim()})`] });
      }
    }
  };

  // R4 (Bilder-Befund) — Bild einfügen: als data-URI direkt ins Markdown
  // (![name](data:image/…)); Quelle ist die Dateiauswahl ODER ein Paste mit
  // Bilddaten (Screenshot). Der Renderer zeigt es, der Serializer erhält es.
  const insertImageFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || "");
      if (!url.startsWith("data:image/")) {
        return;
      }
      const alt = (file.name || "Bild").replace(/[[\]()]/g, "_");
      if (view === "wysiwyg") {
        execWysiwyg("insertHTML", `<img src="${url}" alt="${alt}" />`);
      } else {
        applyMd({ insert: `![${alt}](${url})` });
      }
    };
    reader.readAsDataURL(file);
  };

  const onEditorPaste = (e: React.ClipboardEvent) => {
    const file = Array.from(e.clipboardData?.files || []).find((f) =>
      f.type.startsWith("image/"),
    );
    if (file) {
      e.preventDefault();
      insertImageFile(file);
    }
  };

  // R4 (Punkt 11) — Tab rückt ein statt den Editor zu verlassen.
  const onTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Tab") {
      return;
    }
    e.preventDefault();
    const ta = e.currentTarget;
    const start = ta.selectionStart ?? 0;
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    if (e.shiftKey) {
      const removed = /^( {1,2})/.exec(text.slice(lineStart))?.[1]?.length ?? 0;
      if (removed === 0) {
        return;
      }
      const next = text.slice(0, lineStart) + text.slice(lineStart + removed);
      onChange(next);
      requestAnimationFrame(() =>
        ta.setSelectionRange(start - removed, start - removed),
      );
    } else {
      const next = `${text.slice(0, lineStart)}  ${text.slice(lineStart)}`;
      onChange(next);
      requestAnimationFrame(() => ta.setSelectionRange(start + 2, start + 2));
    }
  };

  const onWysiwygKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") {
      return;
    }
    e.preventDefault();
    // P15 — inside a table, Tab moves to the next/previous cell (as users
    // expect). Indenting here would run execCommand("indent"), which wraps the
    // table in a <blockquote> and destroys it on the next (re)render/serialize.
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    const anchor = sel?.anchorNode ?? null;
    const startEl =
      anchor && anchor.nodeType !== Node.ELEMENT_NODE
        ? anchor.parentElement
        : (anchor as HTMLElement | null);
    const cell = startEl?.closest?.("td,th") as HTMLElement | null;
    if (cell) {
      const target = adjacentTableCell(cell, e.shiftKey);
      if (target && sel) {
        const range = document.createRange();
        range.selectNodeContents(target);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      return;
    }
    execWysiwyg(e.shiftKey ? "outdent" : "indent");
  };

  // Welle B (Bug-Fix) — Links im gerenderten Dokument klickbar machen. Im
  // WYSIWYG-Editor (contentEditable) setzt ein Klick auf ein <a> normalerweise
  // nur den Cursor statt zu navigieren; in der Vorschau würde ein interner
  // Link (/md, /list, /board, /files) einen vollen Reload auslösen. Beides
  // umgeht dieser Handler: interne Routen über den SPA-Router, externe Links
  // (http/https) in einem neuen Tab. Der Frontmatter-Link-Filter (markdown.ts)
  // lässt nur sichere Ziele durch, daher genügt hier die href-Auswertung.
  const onDocLinkClick = (e: React.MouseEvent) => {
    // Welle B (Punkt 15) — ein Vorschau-Chip (WYSIWYG) ist kein <a>; er trägt
    // das Ziel in data-mdhref. Klick öffnet das Zieldokument.
    const chip = (e.target as HTMLElement).closest?.(".ryfe-mdpreview");
    const chipHref = chip?.getAttribute("data-mdhref");
    if (chipHref) {
      e.preventDefault();
      if (chipHref.startsWith("/")) {
        navigate(chipHref);
      } else if (/^https?:\/\//i.test(chipHref)) {
        window.open(chipHref, "_blank", "noopener,noreferrer");
      }
      return;
    }
    const anchor = (e.target as HTMLElement).closest?.("a");
    const href = anchor?.getAttribute("href");
    if (!href) {
      return;
    }
    if (href.startsWith("/")) {
      // Interne RYFE-Route → SPA-Navigation ohne Reload.
      e.preventDefault();
      navigate(href);
      return;
    }
    if (/^https?:\/\//i.test(href) || href.startsWith("mailto:")) {
      // Externes Ziel → neuer Tab (der Editor/das Dokument bleibt erhalten).
      e.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  // Welle B (Punkt 15) — das RYFE-Link-Element unter der aktuellen WYSIWYG-
  // Auswahl (Link/Button/Vorschau-Chip), für den Bearbeiten-Fall.
  const currentLinkEl = (): HTMLElement | null => {
    const root = wysiwygRef.current;
    let node = window.getSelection()?.anchorNode as Node | null;
    while (node && node !== root) {
      if (
        node instanceof HTMLElement &&
        (node.tagName === "A" || node.classList.contains("ryfe-mdpreview"))
      ) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  };

  const settingsFromEl = (el: HTMLElement): LinkSettings => {
    if (el.classList.contains("ryfe-mdpreview")) {
      return {
        type: "preview",
        href: el.getAttribute("data-mdhref") || "",
        label: el.getAttribute("data-mdlabel") || "",
        mode: (el.getAttribute("data-mdmode") as PreviewMode) || "fit",
      };
    }
    return {
      type: el.getAttribute("data-mdtype") === "button" ? "button" : "link",
      href: el.getAttribute("href") || "",
      label: el.textContent || "",
      mode: "fit",
    };
  };

  // 🔗-Toolbar-Button → Link-Darstellungs-Dialog. Steht die Auswahl auf einem
  // bestehenden RYFE-Link (WYSIWYG), wird er bearbeitet, sonst neu eingefügt.
  const openLinkDialog = () => {
    if (view === "wysiwyg") {
      const el = currentLinkEl();
      if (el) {
        setLinkDialog({ initial: settingsFromEl(el), el });
        return;
      }
      const sel = window.getSelection()?.toString() || "";
      setLinkDialog({
        initial: { type: "link", href: "", label: sel, mode: "fit" },
        el: null,
      });
      return;
    }
    const ta = editorRef.current;
    const sel = ta
      ? text.slice(ta.selectionStart ?? 0, ta.selectionEnd ?? 0)
      : "";
    setLinkDialog({
      initial: { type: "link", href: "", label: sel, mode: "fit" },
      el: null,
    });
  };

  const applyLinkDialog = (s: LinkSettings) => {
    const md = linkMarkdown(s);
    if (view === "wysiwyg") {
      // renderMarkdown umschließt ein einzelnes Token mit <p>…</p> — für die
      // Inline-Einfügung/Ersetzung streifen wir genau diese Hülle ab.
      const html = renderMarkdown(md)
        .replace(/^<p>/, "")
        .replace(/<\/p>\s*$/, "");
      const el = linkDialog?.el;
      if (el && el.parentNode) {
        el.outerHTML = html;
        syncFromWysiwyg();
      } else {
        execWysiwyg("insertHTML", html);
      }
    } else {
      const ta = editorRef.current;
      const start = ta?.selectionStart ?? text.length;
      const end = ta?.selectionEnd ?? start;
      const next = text.slice(0, start) + md + text.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta?.focus();
        const pos = start + md.length;
        ta?.setSelectionRange(pos, pos);
      });
    }
    setLinkDialog(null);
  };

  // Welle B (Punkt 15) — Live-Vorschau in der Vorschau/Geteilt-Ansicht: nach
  // jedem Render die `.ryfe-mdpreview`-Platzhalter (nur interne Ziele) durch
  // montierte RyfeDocEmbed-Komponenten ersetzen. Der Guard greift über den
  // normalen Content-Endpoint. Externe URLs bleiben Chip.
  useEffect(() => {
    embedRoots.current.forEach((r) => r.unmount());
    embedRoots.current = [];
    const container = previewRef.current;
    if (!container || !wsId || (view !== "preview" && view !== "split")) {
      return;
    }
    const nodes = Array.from(
      container.querySelectorAll(".ryfe-mdpreview[data-mdhref]"),
    );
    for (const node of nodes) {
      const id = idFromRoute(node.getAttribute("data-mdhref") || "");
      if (!id) {
        continue;
      }
      const mode = (node.getAttribute("data-mdmode") as EmbedMode) || "fit";
      const label = node.getAttribute("data-mdlabel") || "Dokument";
      const host = document.createElement("span");
      node.replaceWith(host);
      const root = createRoot(host);
      root.render(
        <RyfeDocEmbed wsId={wsId} fileId={id} mode={mode} label={label} />,
      );
      embedRoots.current.push(root);
    }
    return () => {
      embedRoots.current.forEach((r) => r.unmount());
      embedRoots.current = [];
    };
  }, [docBody, view, wsId]);

  // Ränder-Einstellung → Innenabstand des Dokuments (Editor + Vorschau).
  const DOC_PADDING: Record<DocSettings["raender"], string> = {
    schmal: "0.75rem",
    normal: "1.25rem",
    breit: "3rem",
  };
  const docPad =
    DOC_PADDING[(docSettings ?? DEFAULT_DOC_SETTINGS).raender] ?? "1.25rem";

  // P8 — in der reinen Vorschau die Seite so zeigen, wie sie im PDF-Export ist:
  // ein Blatt in Seitenbreite mit umlaufenden Rändern (aus denselben
  // pdfOptionsFor-Werten wie der Export), zentriert. pt → px (96/72).
  const pdf = pdfOptionsFor(docSettings ?? DEFAULT_DOC_SETTINGS);
  const ptToPx = (pt: number) => Math.round((pt * 96) / 72);
  const pageView = view === "preview";
  const alignJustify =
    docSettings?.ausrichtung === "blocksatz" ? "justify" : undefined;

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  if (status === "loading") {
    return <div className="ryfe-muted">Lädt…</div>;
  }
  if (status === "error") {
    return <div className="ryfe-auth__error">Notiz nicht gefunden.</div>;
  }

  return (
    <div className="ryfe-board">
      <div className="ryfe-board__bar">
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate(backToFiles(docFolder))}
          title="Zurück in den Ordner der Datei"
        >
          ← Dateien
        </button>
        <span className="ryfe-board__bar__title">{name}</span>
        {/* CC_07 Block 5 (Umbenennen) lebt seit Revision R3 im Datei-Menü. */}
        <span
          className={saveState === "error" ? "ryfe-save-error" : "ryfe-muted"}
          data-testid="md-save-state"
          title={saveError || undefined}
        >
          {saveState === "saving"
            ? "Speichert…"
            : saveState === "saved"
            ? "Gespeichert"
            : saveState === "error"
            ? "⚠ Nicht gespeichert"
            : ""}
        </span>
        <span className="ryfe-topbar__spacer" />
        {(["wysiwyg", "split", "edit", "preview"] as const).map((v) => (
          <button
            key={v}
            className={`ryfe-btn ${
              view === v ? "ryfe-btn--primary" : "ryfe-btn--ghost"
            }`}
            onClick={() => setView(v)}
          >
            {v === "wysiwyg"
              ? "Editor"
              : v === "split"
              ? "Geteilt"
              : v === "edit"
              ? "Text"
              : "Vorschau"}
          </button>
        ))}
        <input
          ref={mdInput}
          type="file"
          accept=".md,.markdown,.txt,text/markdown,text/plain"
          hidden
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) {
              return;
            }
            const content = await f.text();
            setText(content);
            if (view === "wysiwyg") {
              fillWysiwyg(content);
            }
            void save(content);
          }}
        />
        {/* Revision R3 — Datei-Menü: bündelt Umbenennen, Import/Export,
            Speichern, Teilen und die Dokumenteinstellungen. */}
        <div className="ryfe-elmenu__wrap">
          <button
            className="ryfe-btn ryfe-btn--ghost"
            data-testid="md-file-menu"
            onClick={() => setFileMenu((o) => !o)}
          >
            Datei ▾
          </button>
          {fileMenu && (
            <div className="ryfe-elmenu__sub" role="menu">
              <button
                className="ryfe-elmenu__item"
                onClick={async () => {
                  setFileMenu(false);
                  if (!wsId) {
                    return;
                  }
                  const next = window.prompt("Notiz umbenennen", name);
                  if (!next || next === name) {
                    return;
                  }
                  try {
                    await ryfeApi.patchFile(wsId, fileId, { name: next });
                    setName(next);
                  } catch {
                    /* ignore — the note itself is unaffected */
                  }
                }}
              >
                Umbenennen…
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={() => {
                  setFileMenu(false);
                  mdInput.current?.click();
                }}
              >
                ⬆ Markdown importieren…
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={() => {
                  setFileMenu(false);
                  downloadText(safeFilename(name, "md"), "text/markdown", text);
                }}
              >
                ⬇ Als Markdown
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={async () => {
                  setFileMenu(false);
                  // mdToPdf embeds the note's images (decoded browser-side) —
                  // async, unlike the list/text PDF path.
                  downloadBytes(
                    safeFilename(name, "pdf"),
                    "application/pdf",
                    await mdToPdf(name, docBody, pdfOptionsFor(docSettings)),
                  );
                }}
              >
                ⬇ Als PDF
              </button>
              <button
                className="ryfe-elmenu__item"
                data-testid="md-doc-settings"
                onClick={() => {
                  setFileMenu(false);
                  setDocDialog(docSettings ?? DEFAULT_DOC_SETTINGS);
                }}
              >
                Dokumenteinstellungen…
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={() => {
                  setFileMenu(false);
                  void save(text);
                }}
              >
                Speichern
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={() => {
                  setFileMenu(false);
                  setShareOpen(true);
                }}
              >
                Teilen…
              </button>
            </div>
          )}
        </div>
      </div>

      {saveError && (
        <div className="ryfe-save-banner" role="alert">
          <span>{saveError}</span>
          <button
            className="ryfe-btn ryfe-btn--ghost"
            onClick={() => void save(text)}
          >
            Erneut speichern
          </button>
        </div>
      )}

      {/* P11.1 (Lane-C-Mock) — MD-Toolbar. MD-taugliche Untermenge des Mocks:
          Absatzstil, Auszeichnung, Elemente (Checkliste/Tabelle). Font-Picker,
          Ausrichtung und Seitenformat sind in Markdown nicht abbildbar und
          liegen beim P10-Export (dokumentierte Abweichung). */}
      {view !== "preview" && (
        <div className="ryfe-mdbar" data-testid="md-toolbar">
          <div className="ryfe-elmenu__seg">
            <button
              className="ryfe-elmenu__size"
              title="Absatz"
              onClick={() => format("p")}
            >
              ¶
            </button>
            {[1, 2, 3].map((h) => (
              <button
                key={h}
                className="ryfe-elmenu__size"
                title={`Überschrift ${h}`}
                onClick={() => format(`h${h}` as "h1" | "h2" | "h3")}
              >
                H{h}
              </button>
            ))}
            {/* Fidelity (15-notiz-editor) — „Zitat"-Button (Blockquote). */}
            <button
              className="ryfe-elmenu__size"
              title="Zitat"
              onClick={() => format("quote")}
            >
              ❝
            </button>
          </div>
          <div className="ryfe-elmenu__seg">
            <button
              className="ryfe-elmenu__size"
              title="Fett"
              onClick={() => format("bold")}
            >
              <b>F</b>
            </button>
            <button
              className="ryfe-elmenu__size"
              title="Kursiv"
              onClick={() => format("italic")}
            >
              <i>K</i>
            </button>
            <button
              className="ryfe-elmenu__size"
              title="Code"
              onClick={() => format("code")}
            >
              {"</>"}
            </button>
          </div>
          <div className="ryfe-elmenu__seg">
            <button
              className="ryfe-elmenu__size"
              title="Liste"
              onClick={() => format("ul")}
            >
              • Liste
            </button>
            <button
              className="ryfe-elmenu__size"
              title="Checkliste"
              onClick={() => format("check")}
            >
              ☑ Checkliste
            </button>
            <button
              className="ryfe-elmenu__size"
              title="Tabelle einfügen"
              onClick={() => format("table")}
            >
              ▦ Tabelle
            </button>
          </div>
          <div className="ryfe-elmenu__seg">
            <button
              className="ryfe-elmenu__size"
              title="Link / Button / Vorschau einfügen oder ändern"
              data-testid="md-link"
              onClick={openLinkDialog}
            >
              🔗 Link
            </button>
            <button
              className="ryfe-elmenu__size"
              title="Bild einfügen (oder Screenshot einfach einfügen/pasten)"
              data-testid="md-image"
              onClick={() => imgInput.current?.click()}
            >
              🖼 Bild
            </button>
            {/* Welle B — create a linked file (Board/Notiz/Liste) in this
                note's folder and drop an inline link to it. */}
            <button
              className="ryfe-elmenu__size"
              title="Datei erstellen und hier verlinken"
              data-testid="md-create-file"
              onClick={() => setCreating(true)}
            >
              ＋ Datei
            </button>
          </div>
        </div>
      )}
      <input
        ref={imgInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) {
            insertImageFile(f);
          }
        }}
      />

      <div className={`ryfe-md ryfe-md--${view}`}>
        {/* Revision R3 — WYSIWYG: direkt im gerenderten Dokument schreiben.
            Der Inhalt kommt aus renderMarkdown (escaped, Link-Schema-Filter);
            jede Eingabe wird via domToMarkdown zurückserialisiert. */}
        {view === "wysiwyg" && (
          <div
            ref={wysiwygRef}
            className="ryfe-md__preview ryfe-md__wysiwyg"
            data-testid="md-wysiwyg"
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            style={{
              padding: `1rem ${docPad}`,
              textAlign:
                docSettings?.ausrichtung === "blocksatz"
                  ? "justify"
                  : undefined,
            }}
            onInput={syncFromWysiwyg}
            onBlur={syncFromWysiwyg}
            onPaste={onEditorPaste}
            onKeyDown={onWysiwygKeyDown}
            onClick={(e) => {
              // R4 (Punkt 11) — Klick auf eine Checkliste-Checkbox toggelt den
              // DOM-Zustand; der löst kein input-Event aus → explizit syncen.
              const t = e.target as HTMLElement;
              if (t instanceof HTMLInputElement && t.type === "checkbox") {
                setTimeout(syncFromWysiwyg, 0);
                return;
              }
              // Welle B (Bug-Fix) — Klick auf einen Link navigiert (statt nur
              // den Cursor zu setzen); interne Routen ohne Reload.
              onDocLinkClick(e);
            }}
          />
        )}
        {(view === "split" || view === "edit") && (
          <textarea
            ref={editorRef}
            className="ryfe-md__editor"
            data-testid="md-editor"
            value={text}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
            onPaste={onEditorPaste}
            onKeyDown={onTextareaKeyDown}
            placeholder="# Überschrift&#10;&#10;Text mit **fett**, *kursiv*, `code`, Listen und [Links](https://…)."
          />
        )}
        {(view === "split" || view === "preview") && (
          <div
            ref={previewRef}
            className={`ryfe-md__preview${
              pageView ? " ryfe-md__preview--page" : ""
            }`}
            data-testid="md-preview"
            data-pageview={pageView ? "1" : undefined}
            style={
              pageView
                ? {
                    // P8 — ein Blatt in echter Seitengröße (Breite UND Höhe aus
                    // pdfOptionsFor) mit PDF-Rändern, zentriert auf dem
                    // kontrastierenden „Schreibtisch" (--preview-Hintergrund). So
                    // entspricht die Ansicht der PDF-Export-Seite.
                    width: `${ptToPx(pdf.pageW)}px`,
                    maxWidth: "100%",
                    minHeight: `${ptToPx(pdf.pageH)}px`,
                    margin: "1.25rem auto",
                    padding: `${ptToPx(pdf.margin)}px`,
                    boxSizing: "border-box",
                    background: "var(--ryfe-surface)",
                    boxShadow: "var(--ryfe-shadow-lg)",
                    borderRadius: "3px",
                    textAlign: alignJustify,
                  }
                : {
                    padding: `1rem ${docPad}`,
                    textAlign: alignJustify,
                  }
            }
            // Welle B (Bug-Fix) — interne Links per SPA-Router öffnen (kein
            // Reload), externe in neuem Tab.
            onClick={onDocLinkClick}
            // Der Renderer escaped jede Eingabe, bevor er Markup erzeugt, und
            // lässt nur http/https/mailto als Linkziel zu (markdown.ts).
            dangerouslySetInnerHTML={{ __html: renderMarkdown(docBody) }}
          />
        )}
      </div>

      {/* Revision R3 — Dokumenteinstellungen (Seitenformat + Ränder), als
          Frontmatter im Markdown gespeichert; wirkt auf PDF-Export + Ansicht. */}
      {docDialog && (
        <div
          className="ryfe-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setDocDialog(null)}
        >
          <div
            className="ryfe-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="ryfe-panel__title">Dokumenteinstellungen</h2>
            <label className="ryfe-field-label">
              Seitenformat
              <select
                className="ryfe-select"
                value={docDialog.seite}
                aria-label="Seitenformat"
                onChange={(e) =>
                  setDocDialog({
                    ...docDialog,
                    seite: e.target.value as DocSettings["seite"],
                  })
                }
              >
                <option value="a4">A4 Hochformat</option>
                <option value="a4quer">A4 Querformat</option>
                <option value="letter">Letter</option>
              </select>
            </label>
            <label className="ryfe-field-label">
              Ränder
              <select
                className="ryfe-select"
                value={docDialog.raender}
                aria-label="Ränder"
                onChange={(e) =>
                  setDocDialog({
                    ...docDialog,
                    raender: e.target.value as DocSettings["raender"],
                  })
                }
              >
                <option value="schmal">Schmal</option>
                <option value="normal">Normal</option>
                <option value="breit">Breit</option>
              </select>
            </label>
            <label className="ryfe-field-label">
              Ausrichtung
              <select
                className="ryfe-select"
                value={docDialog.ausrichtung ?? "links"}
                aria-label="Ausrichtung"
                onChange={(e) =>
                  setDocDialog({
                    ...docDialog,
                    ausrichtung: e.target.value as DocSettings["ausrichtung"],
                  })
                }
              >
                <option value="links">Linksbündig</option>
                <option value="blocksatz">Blocksatz</option>
              </select>
            </label>
            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--ghost"
                onClick={() => setDocDialog(null)}
              >
                Abbrechen
              </button>
              <button
                className="ryfe-btn ryfe-btn--primary"
                data-testid="md-doc-apply"
                onClick={() => {
                  onChange(withDocSettings(text, docDialog));
                  setDocDialog(null);
                }}
              >
                Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {shareOpen && (
        <ShareSubmenu
          target={{ kind: "file", id: fileId, name }}
          wsId={wsId ?? undefined}
          onClose={() => setShareOpen(false)}
          onInviteByEmail={
            wsId
              ? async (email, role) => {
                  await ryfeApi.inviteToFile(
                    wsId,
                    fileId,
                    email,
                    role === "editor" ? "editor" : "viewer",
                  );
                }
              : undefined
          }
          onCreateSnapshot={
            wsId
              ? () =>
                  createEncryptedSnapshot({
                    workspaceId: wsId,
                    fileId,
                    snapshot: { kind: "md", name, content: text },
                  })
              : undefined
          }
        />
      )}

      {/* Welle B — create a file in this note's folder and link it inline. */}
      {creating && (
        <AddFileDialog
          busy={createBusy}
          onClose={() => setCreating(false)}
          onCreate={createLinked}
        />
      )}

      {/* Welle B (Punkt 15) — Link/Button/Vorschau-Darstellung wählen. */}
      {linkDialog && (
        <LinkSettingsDialog
          initial={linkDialog.initial}
          onClose={() => setLinkDialog(null)}
          onApply={applyLinkDialog}
        />
      )}
    </div>
  );
};
